/**
 * botDetector.js — 前端底层硬件指纹与生物动力学探测器 (Anti-Bot Sensor)
 *
 * 遵循 SOLID 原则，按传感器维度职责拆分：
 * 1. WebGLSensor: 硬件 GPU 与 CPU 软件模拟渲染器探测
 * 2. EnvironmentSensor: 无头自动化环境与原型链劫持痕迹探测
 * 3. BiometricsSensor: 人类物理生物行为学（isTrusted 物理事件、键盘/触摸/鼠标轨迹动力学）
 * 4. BotDetector: 聚合各传感器指标并生成防篡改安全证明包 (BotProof)
 */

import { getPoWPayload } from './powWorker.js';

const BOT_GUARD_SALT = 'DS_BOT_GUARD_SALT_V1_#20260906!';
const MAX_SAMPLES = 20;

// 全局行为动力学状态追踪单例
const behaviorState = {
  initialized: false,
  startTime: Date.now(),
  firstActiveTime: 0,
  lastEventTime: 0,
  lastIsTrusted: false,
  interactionCount: 0,
  touchDetected: false,
  keyboardDetected: false,
  points: [], // [{ x, y, t, isTouch }]
  lastSampleTime: 0,
  touchDuration: 0,
  touchRadius: 0,
  touchDrift: 0,
  touchForce: 0,
};

/**
 * 监听全量用户交互（鼠标、触摸、键盘），确保人类真实物理行为被完整覆盖，杜绝误伤
 */
function initBehaviorTracker() {
  if (behaviorState.initialized || typeof window === 'undefined') return;
  behaviorState.initialized = true;
  behaviorState.startTime = Date.now();

  const markTrustedInteraction = (e, isTouch = false, isKey = false) => {
    const now = Date.now();
    if (!behaviorState.firstActiveTime) {
      behaviorState.firstActiveTime = now;
    }
    behaviorState.lastEventTime = now;
    behaviorState.interactionCount += 1;
    if (e && e.isTrusted) {
      behaviorState.lastIsTrusted = true;
    }
    if (isTouch) {
      behaviorState.touchDetected = true;
    }
    if (isKey) {
      behaviorState.keyboardDetected = true;
    }
  };

  const handlePointer = (e, isTouch = false) => {
    markTrustedInteraction(e, isTouch, false);
    const now = Date.now();
    // 节流采样：每 30ms 最多采样一次轨迹点，避免高频移动消耗 CPU
    if (now - behaviorState.lastSampleTime < 30) return;
    behaviorState.lastSampleTime = now;

    let x = 0;
    let y = 0;
    if (e.touches && e.touches.length > 0) {
      x = Math.round(e.touches[0].clientX);
      y = Math.round(e.touches[0].clientY);
    } else if (typeof e.clientX === 'number') {
      x = Math.round(e.clientX);
      y = Math.round(e.clientY);
    } else {
      return;
    }

    behaviorState.points.push({ x, y, t: now, isTouch });
    if (behaviorState.points.length > MAX_SAMPLES) {
      behaviorState.points.shift();
    }
  };

  const handleTouch = (e) => handlePointer(e, true);
  const handleMouse = (e) => handlePointer(e, false);
  const handleKey = (e) => markTrustedInteraction(e, false, true);

  let touchStartTimestamp = 0;
  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartRadius = 0;
  let touchStartForce = 0;

  const handleTouchStart = (e) => {
    markTrustedInteraction(e, true, false);
    if (e.touches && e.touches.length > 0) {
      const t = e.touches[0];
      touchStartTimestamp = typeof performance !== 'undefined' ? performance.now() : Date.now();
      touchStartX = t.clientX || 0;
      touchStartY = t.clientY || 0;
      touchStartRadius = Math.max(t.radiusX || 0, t.radiusY || 0);
      touchStartForce = t.force || 0;
      handlePointer(e, true);
    }
  };

  const handleTouchEnd = (e) => {
    markTrustedInteraction(e, true, false);
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (touchStartTimestamp > 0) {
      const dur = Math.round(now - touchStartTimestamp);
      behaviorState.touchDuration = Math.max(behaviorState.touchDuration, dur);
      const t = (e.changedTouches && e.changedTouches[0]) || {};
      const endX = t.clientX ?? touchStartX;
      const endY = t.clientY ?? touchStartY;
      const dx = endX - touchStartX;
      const dy = endY - touchStartY;
      const drift = Math.sqrt(dx * dx + dy * dy);
      behaviorState.touchDrift = Math.max(behaviorState.touchDrift, drift);
      behaviorState.touchRadius = Math.max(
        behaviorState.touchRadius,
        touchStartRadius,
        t.radiusX || 0,
        t.radiusY || 0
      );
      behaviorState.touchForce = Math.max(behaviorState.touchForce, touchStartForce, t.force || 0);
      touchStartTimestamp = 0;
    }
  };

  const opts = { passive: true, capture: true };
  window.addEventListener('pointermove', handleMouse, opts);
  window.addEventListener('mousemove', handleMouse, opts);
  window.addEventListener('touchmove', handleTouch, opts);
  window.addEventListener('pointerdown', handleMouse, opts);
  window.addEventListener('touchstart', handleTouchStart, opts);
  window.addEventListener('touchend', handleTouchEnd, opts);
  window.addEventListener('touchcancel', handleTouchEnd, opts);
  window.addEventListener('keydown', handleKey, opts);
  window.addEventListener('keyup', handleKey, opts);
  window.addEventListener(
    'click',
    (e) => {
      markTrustedInteraction(e, false, false);
    },
    opts
  );
}

// 自动初始化全局追踪器
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBehaviorTracker);
  } else {
    initBehaviorTracker();
  }
}

/**
 * WebGL 传感器：探测底层 GPU 硬件指纹，嗅探 Linux/Docker 无显卡服务器中的 CPU 软件模拟渲染器
 */
export function getWebGLFingerprint() {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return { vendor: '', renderer: '', isSoftware: false };
    }
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) {
      return { vendor: '', renderer: '', isSoftware: false };
    }
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) {
      return { vendor: '', renderer: '', isSoftware: false };
    }
    const vendor = String(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '').trim();
    const renderer = String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '').trim();
    const combined = `${vendor} ${renderer}`.toLowerCase();

    // 严密匹配主流云端无卡无头 CPU 模拟渲染器与虚拟机驱动
    const isSoftware =
      /swiftshader|llvmpipe|softpipe|mesa off-screen|virtualbox|vmware|qemu|microsoft basic render|mesa dri 200|gallium.*llvmpipe/i.test(
        combined
      );

    return { vendor, renderer, isSoftware };
  } catch {
    return { vendor: '', renderer: '', isSoftware: false };
  }
}

/**
 * 环境传感器：探测自动化脚本特征（webdriver、stealth 原型链修改、视口外框尺寸）
 */
export function getEnvironmentProfile() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      webdriver: false,
      outerZero: false,
      isTouch: false,
      isKeyboard: false,
      pluginsLen: 0,
      languagesLen: 0,
    };
  }

  // 1. webdriver 深度属性检查（含 stealth 篡改原型链痕迹）
  let webdriver = false;
  try {
    if (navigator.webdriver === true) {
      webdriver = true;
    }
    if (Object.prototype.hasOwnProperty.call(navigator, 'webdriver')) {
      webdriver = true;
    }
    if (document.documentElement.getAttribute('webdriver') !== null) {
      webdriver = true;
    }
  } catch {
    webdriver = false;
  }

  // 2. 窗口外框尺寸矛盾（无头环境未适配物理桌面常为 0x0）
  let outerZero = false;
  try {
    if (window.outerWidth === 0 && window.outerHeight === 0) {
      outerZero = true;
    }
  } catch {
    outerZero = false;
  }

  // 3. 触屏与实体键盘能力（真实移动设备与 PC 的交互凭证）
  const isTouch = Boolean(
    behaviorState.touchDetected ||
      'ontouchstart' in window ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 0)
  );

  const isKeyboard = Boolean(behaviorState.keyboardDetected);

  const pluginsLen = Array.isArray(navigator.plugins)
    ? navigator.plugins.length
    : navigator.plugins?.length || 0;
  const languagesLen = Array.isArray(navigator.languages)
    ? navigator.languages.length
    : navigator.language
    ? 1
    : 0;

  return {
    webdriver,
    outerZero,
    isTouch,
    isKeyboard,
    pluginsLen,
    languagesLen,
  };
}

/**
 * 生物动力学传感器：分析人类操作真实性与轨迹动力学指标
 * @param {Event} [triggerEvent] 触发当前操作的原生 DOM Event
 */
export function analyzeBehaviorBiometrics(triggerEvent) {
  const now = Date.now();

  // 1. 物理事件真伪判定
  let isTrusted = false;
  if (triggerEvent && typeof triggerEvent.isTrusted === 'boolean') {
    isTrusted = triggerEvent.isTrusted;
  } else {
    isTrusted = behaviorState.lastIsTrusted;
  }

  const pts = behaviorState.points;
  const sampleCount = pts.length;
  const dwell =
    behaviorState.firstActiveTime > 0
      ? now - behaviorState.firstActiveTime
      : now - behaviorState.startTime;

  if (sampleCount < 2) {
    return {
      isTrusted,
      sampleCount,
      totalDistance: 0,
      straightness: 1,
      variance: 0,
      dwellMs: dwell,
      interactions: behaviorState.interactionCount,
      dur: behaviorState.touchDuration || 0,
      rad: Math.round((behaviorState.touchRadius || 0) * 10) / 10,
      drf: Math.round((behaviorState.touchDrift || 0) * 10) / 10,
      frc: Math.round((behaviorState.touchForce || 0) * 100) / 100,
    };
  }

  let totalDist = 0;
  const speeds = [];

  for (let i = 1; i < sampleCount; i += 1) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    const dt = Math.max(pts[i].t - pts[i - 1].t, 1);
    const dist = Math.sqrt(dx * dx + dy * dy);
    totalDist += dist;
    speeds.push(dist / dt); // 速度：像素/毫秒
  }

  const startPt = pts[0];
  const endPt = pts[sampleCount - 1];
  const straightDist = Math.sqrt(
    Math.pow(endPt.x - startPt.x, 2) + Math.pow(endPt.y - startPt.y, 2)
  );

  // 直线度比值：真人必然有微抖动和自然弧度，机械直线插值极其接近 1.000
  const straightness = totalDist / (straightDist + 0.001);

  // 速度方差：真人必有加速与减速阶段，恒速插值方差接近 0
  let speedVariance = 0;
  if (speeds.length > 1) {
    const avgSpeed = speeds.reduce((acc, v) => acc + v, 0) / speeds.length;
    const sumSq = speeds.reduce((acc, v) => acc + Math.pow(v - avgSpeed, 2), 0);
    speedVariance = sumSq / speeds.length;
  }

  return {
    isTrusted,
    sampleCount,
    totalDistance: Math.round(totalDist * 10) / 10,
    straightness: Math.round(straightness * 100) / 100,
    variance: Math.round(speedVariance * 1000) / 1000,
    dwellMs: dwell,
    interactions: behaviorState.interactionCount,
    dur: behaviorState.touchDuration || 0,
    rad: Math.round((behaviorState.touchRadius || 0) * 10) / 10,
    drf: Math.round((behaviorState.touchDrift || 0) * 10) / 10,
    frc: Math.round((behaviorState.touchForce || 0) * 100) / 100,
  };
}

/**
 * 纯 JS 轻量 HMAC-SHA256 签名（支持 WebCrypto 与降级哈希）
 */
async function computeHmacSha256(keyStr, dataStr) {
  try {
    const cryptoObj =
      (typeof window !== 'undefined' && window.crypto) ||
      (typeof globalThis !== 'undefined' && globalThis.crypto);
    if (cryptoObj && cryptoObj.subtle) {
      const enc = new TextEncoder();
      const key = await cryptoObj.subtle.importKey(
        'raw',
        enc.encode(keyStr),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      );
      const signature = await cryptoObj.subtle.sign('HMAC', key, enc.encode(dataStr));
      return Array.from(new Uint8Array(signature))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }
  } catch {
    // WebCrypto 不可用或非安全上下文时降级
  }

  // 降级哈希算法
  let hash = 0;
  const str = keyStr + dataStr;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(16);
}

/**
 * 生成防篡改安全证明包 (BotProof)
 * @param {string} [action] 业务动作名称，如 'trial_claim', 'welcome_pack_claim', 'auth_login'
 * @param {Event} [triggerEvent] 触发的原生 DOM 事件
 * @param {Object} [customPoW] 自定义求解好的 PoW 包（若为空则内部按需自动下发求解）
 * @returns {Promise<string>} 紧凑加密 Base64 字符串
 */
export async function generateBotProof(action = 'generic', triggerEvent = null, customPoW = null) {
  initBehaviorTracker();

  const wgl = getWebGLFingerprint();
  const env = getEnvironmentProfile();
  const bio = analyzeBehaviorBiometrics(triggerEvent);

  const payload = {
    ts: Date.now(),
    nonce: Math.random().toString(36).slice(2, 10),
    act: String(action || 'generic').slice(0, 32),
    wgl: {
      vnd: String(wgl.vendor || '').slice(0, 64),
      ren: String(wgl.renderer || '').slice(0, 128),
      sft: wgl.isSoftware ? 1 : 0,
    },
    env: {
      wd: env.webdriver ? 1 : 0,
      oz: env.outerZero ? 1 : 0,
      tch: env.isTouch ? 1 : 0,
      kbd: env.isKeyboard ? 1 : 0,
      plg: Number(env.pluginsLen) || 0,
      lng: Number(env.languagesLen) || 0,
    },
    bio: {
      tru: bio.isTrusted ? 1 : 0,
      smp: Number(bio.sampleCount) || 0,
      dst: Number(bio.totalDistance) || 0,
      str: Number(bio.straightness) || 1,
      var: Number(bio.variance) || 0,
      dwl: Number(bio.dwellMs) || 0,
      itr: Number(bio.interactions) || 0,
      dur: Number(bio.dur) || 0,
      rad: Number(bio.rad) || 0,
      drf: Number(bio.drf) || 0,
      frc: Number(bio.frc) || 0,
    },
  };

  // 针对敏感动作（注册登录、领试金、领礼包、发验证码）自动求解服务端动态 PoW
  const needsPoW =
    action === 'auth_login' ||
    action === 'trial_claim' ||
    action === 'welcome_pack_claim' ||
    action === 'auth_email_code';
  let pow = customPoW;
  if (!pow && needsPoW) {
    try {
      pow = await getPoWPayload(action);
    } catch (e) {
      console.warn('[PoW] Auto-solve failed:', e);
    }
  }
  if (pow) {
    payload.pow = pow;
  } else if (needsPoW && (action === 'auth_login' || action === 'trial_claim')) {
    // 不交残缺票（无 pow 必 816）。抛错让用户重试；脚本同样过不了。
    throw new Error('安全校验准备失败，请检查网络后重试');
  }

  const jsonStr = JSON.stringify(payload);
  let b64 = '';
  try {
    b64 = btoa(unescape(encodeURIComponent(jsonStr)));
  } catch {
    b64 = btoa(jsonStr);
  }

  const sig = await computeHmacSha256(BOT_GUARD_SALT, b64);
  return `${b64}.${sig.slice(0, 32)}`;
}
